import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Cell,
  PieChart,
  Pie,
  Legend,
} from "recharts";
import Navbar from "../components/ui/Navbar";
import InputField from "../components/ui/InputField";
import SelectField from "../components/ui/SelectField";
import {
  ArrowLeft,
  RefreshCw,
  BarChart2,
  Clock,
  CheckCircle2,
  AlertCircle,
  Users,
  TrendingUp,
  ShieldCheck,
  Ticket,
} from "lucide-react";

const API = process.env.REACT_APP_API_URL;

const ESTADO_COLORS = {
  Nuevo: "#3B82F6",
  "En revisión": "#8B5CF6",
  "En atención": "#6366F1",
  "En espera cliente": "#F59E0B",
  Resuelto: "#10B981",
  Cerrado: "#9CA3AF",
  Cancelado: "#EF4444",
};

const PRIORIDAD_COLORS = {
  Baja: "#10B981",
  Media: "#F59E0B",
  Alta: "#F97316",
  Crítica: "#EF4444",
};

// ── KPI card ─────────────────────────────────────────────────────────────────
function KpiCard({ icon: Icon, label, value, sub, color = "blue" }) {
  const palette = {
    blue: {
      bg: "bg-blue-50",
      border: "border-blue-100",
      icon: "text-blue-500",
      val: "text-blue-700",
    },
    green: {
      bg: "bg-green-50",
      border: "border-green-100",
      icon: "text-green-500",
      val: "text-green-700",
    },
    orange: {
      bg: "bg-orange-50",
      border: "border-orange-100",
      icon: "text-orange-500",
      val: "text-orange-700",
    },
    purple: {
      bg: "bg-purple-50",
      border: "border-purple-100",
      icon: "text-purple-500",
      val: "text-purple-700",
    },
    red: {
      bg: "bg-red-50",
      border: "border-red-100",
      icon: "text-red-500",
      val: "text-red-700",
    },
    gray: {
      bg: "bg-gray-50",
      border: "border-gray-100",
      icon: "text-gray-400",
      val: "text-gray-600",
    },
  };
  const c = palette[color] || palette.blue;
  return (
    <div className={`rounded-xl p-4 border ${c.bg} ${c.border} flex flex-col gap-1`}>
      <div className={`flex items-center gap-1.5 ${c.icon}`}>
        <Icon className="h-4 w-4" />
        <span className="text-xs font-medium text-gray-500 leading-none">
          {label}
        </span>
      </div>
      <p className={`text-2xl font-bold leading-none mt-1 ${c.val}`}>
        {value ?? "—"}
      </p>
      {sub && <p className="text-xs text-gray-400">{sub}</p>}
    </div>
  );
}

// ── Tooltip personalizado para gráficas ──────────────────────────────────────
function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-gray-100 shadow-lg rounded-lg px-3 py-2 text-sm">
      <p className="font-semibold text-gray-700 mb-1">{label}</p>
      {payload.map((p) => (
        <p key={p.name} style={{ color: p.fill || p.color }}>
          {p.name}: <span className="font-bold">{p.value}</span>
        </p>
      ))}
    </div>
  );
}

// ── Sección contenedora de gráfica ───────────────────────────────────────────
function ChartCard({ title, children, empty }) {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
      <h3 className="text-sm font-semibold text-gray-700 mb-4">{title}</h3>
      {empty ? (
        <p className="text-sm text-gray-400 text-center py-10">Sin datos</p>
      ) : (
        children
      )}
    </div>
  );
}

// ── Indicador SLA ────────────────────────────────────────────────────────────
function SlaIndicator({ value }) {
  if (value === null || value === undefined)
    return <span className="text-gray-400 text-sm">Sin datos</span>;
  const color =
    value >= 80
      ? "bg-green-500"
      : value >= 60
        ? "bg-yellow-400"
        : "bg-red-500";
  return (
    <div className="mt-2 space-y-1">
      <div className="flex justify-between text-xs text-gray-500">
        <span>Cumplimiento</span>
        <span className="font-semibold">{value}%</span>
      </div>
      <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all ${color}`}
          style={{ width: `${value}%` }}
        />
      </div>
    </div>
  );
}

// ── Página principal ─────────────────────────────────────────────────────────
export default function SoporteMetricasPage() {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}` };

  const [metricas, setMetricas] = useState(null);
  const [loading, setLoading] = useState(false);
  const [clientes, setClientes] = useState([]);
  const [filtros, setFiltros] = useState({
    fechaDesde: "",
    fechaHasta: "",
    cliente: "",
  });

  const fetchMetricas = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filtros.fechaDesde) params.fechaDesde = filtros.fechaDesde;
      if (filtros.fechaHasta) params.fechaHasta = filtros.fechaHasta;
      if (filtros.cliente) params.cliente = filtros.cliente;
      const { data } = await axios.get(`${API}/soporte/metricas`, {
        headers,
        params,
      });
      setMetricas(data);
    } catch {
      toast.error("Error al cargar métricas");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line
  }, [filtros]);

  useEffect(() => {
    fetchMetricas();
    axios
      .get(`${API}/clientes`, { headers })
      .then(({ data }) => setClientes(data || []))
      .catch(() => {});
    // eslint-disable-next-line
  }, [fetchMetricas]);

  const backlogEstadoData = metricas
    ? Object.entries(metricas.backlogEstado).map(([estado, count]) => ({
        estado,
        count,
      }))
    : [];

  const backlogPrioridadData = metricas
    ? Object.entries(metricas.backlogPrioridad).map(([prioridad, count]) => ({
        prioridad,
        count,
      }))
    : [];

  const slaColor =
    metricas?.pctSlaRespuesta >= 80
      ? "green"
      : metricas?.pctSlaRespuesta >= 60
        ? "orange"
        : "gray";

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="container mx-auto px-4 py-6 pt-20 md:pt-28">

        {/* Header */}
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate("/soporte")}
              className="p-2 rounded-lg border border-gray-200 hover:bg-gray-100 transition text-gray-600"
            >
              <ArrowLeft className="h-4 w-4" />
            </button>
            <div className="p-2 bg-indigo-600 rounded-xl">
              <BarChart2 className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                Métricas de Soporte
              </h1>
              <p className="text-sm text-gray-500">
                Indicadores de desempeño del módulo de soporte técnico
              </p>
            </div>
          </div>
          <button
            onClick={fetchMetricas}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-100 transition disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            Actualizar
          </button>
        </div>

        {/* Filtros */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 mb-6">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <InputField
              label="Desde"
              type="date"
              value={filtros.fechaDesde}
              onChange={(e) =>
                setFiltros((p) => ({ ...p, fechaDesde: e.target.value }))
              }
            />
            <InputField
              label="Hasta"
              type="date"
              value={filtros.fechaHasta}
              onChange={(e) =>
                setFiltros((p) => ({ ...p, fechaHasta: e.target.value }))
              }
            />
            <SelectField
              label="Cliente"
              name="cliente"
              value={filtros.cliente}
              options={clientes}
              onChange={(e) =>
                setFiltros((p) => ({ ...p, cliente: e.target.value }))
              }
              placeholder="Todos los clientes"
            />
          </div>
        </div>

        {/* Cargando */}
        {loading && (
          <div className="flex items-center justify-center py-24">
            <div className="flex flex-col items-center gap-3 text-indigo-500">
              <RefreshCw className="h-8 w-8 animate-spin" />
              <span className="text-sm text-gray-500">Calculando métricas…</span>
            </div>
          </div>
        )}

        {/* Contenido */}
        {!loading && metricas && (
          <div className="space-y-6">

            {/* ── KPIs ─────────────────────────────────────────────────── */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
              <KpiCard
                icon={Ticket}
                label="Total tickets"
                value={metricas.totalTickets}
                color="blue"
              />
              <KpiCard
                icon={AlertCircle}
                label="Abiertos"
                value={metricas.abiertos}
                sub="en curso"
                color="orange"
              />
              <KpiCard
                icon={CheckCircle2}
                label="Resueltos"
                value={metricas.resueltos}
                color="green"
              />
              <KpiCard
                icon={Clock}
                label="TMR"
                value={
                  metricas.tmrHoras ? `${metricas.tmrHoras}h` : "—"
                }
                sub="primera respuesta"
                color="purple"
              />
              <KpiCard
                icon={TrendingUp}
                label="TME"
                value={
                  metricas.tmeHoras ? `${metricas.tmeHoras}h` : "—"
                }
                sub="tiempo resolución"
                color="blue"
              />
              <KpiCard
                icon={ShieldCheck}
                label="SLA Respuesta"
                value={
                  metricas.pctSlaRespuesta !== null
                    ? `${metricas.pctSlaRespuesta}%`
                    : "—"
                }
                sub="cumplimiento"
                color={slaColor}
              />
            </div>

            {/* ── SLA visual ────────────────────────────────────────────── */}
            {metricas.pctSlaRespuesta !== null && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
                <div className="flex items-center gap-2 mb-3">
                  <ShieldCheck className="h-4 w-4 text-indigo-500" />
                  <h3 className="text-sm font-semibold text-gray-700">
                    Cumplimiento SLA de respuesta
                  </h3>
                </div>
                <SlaIndicator value={metricas.pctSlaRespuesta} />
                <p className="text-xs text-gray-400 mt-2">
                  Porcentaje de tickets que recibieron primera respuesta dentro
                  del tiempo acordado por prioridad.
                </p>
              </div>
            )}

            {/* ── Gráficas ──────────────────────────────────────────────── */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

              {/* Tickets por estado */}
              <ChartCard
                title="Distribución por estado"
                empty={backlogEstadoData.length === 0}
              >
                <ResponsiveContainer width="100%" height={230}>
                  <BarChart
                    data={backlogEstadoData}
                    margin={{ left: -15, right: 8 }}
                  >
                    <XAxis
                      dataKey="estado"
                      tick={{ fontSize: 10 }}
                      interval={0}
                      angle={-20}
                      textAnchor="end"
                      height={50}
                    />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="count" name="Tickets" radius={[4, 4, 0, 0]}>
                      {backlogEstadoData.map((entry) => (
                        <Cell
                          key={entry.estado}
                          fill={ESTADO_COLORS[entry.estado] || "#8884d8"}
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </ChartCard>

              {/* Tickets abiertos por prioridad */}
              <ChartCard
                title="Tickets abiertos por prioridad"
                empty={backlogPrioridadData.length === 0}
              >
                <ResponsiveContainer width="100%" height={230}>
                  <PieChart>
                    <Pie
                      data={backlogPrioridadData}
                      dataKey="count"
                      nameKey="prioridad"
                      cx="50%"
                      cy="45%"
                      outerRadius={80}
                      innerRadius={36}
                      paddingAngle={3}
                      label={({ prioridad, percent }) =>
                        `${prioridad} ${(percent * 100).toFixed(0)}%`
                      }
                      labelLine={false}
                    >
                      {backlogPrioridadData.map((entry) => (
                        <Cell
                          key={entry.prioridad}
                          fill={PRIORIDAD_COLORS[entry.prioridad] || "#8884d8"}
                        />
                      ))}
                    </Pie>
                    <Legend
                      iconType="circle"
                      iconSize={8}
                      wrapperStyle={{ fontSize: 12 }}
                    />
                    <Tooltip
                      formatter={(value, name) => [value, name]}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </ChartCard>
            </div>

            {/* ── Horas por técnico ─────────────────────────────────────── */}
            {metricas.horasTecnico?.length > 0 && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
                <div className="flex items-center gap-2 mb-4">
                  <Users className="h-4 w-4 text-indigo-500" />
                  <h3 className="text-sm font-semibold text-gray-700">
                    Tiempo y costo por técnico
                  </h3>
                </div>

                {/* Mini bar chart de minutos */}
                <ResponsiveContainer width="100%" height={160}>
                  <BarChart
                    data={[...metricas.horasTecnico].sort(
                      (a, b) => b.minutos - a.minutos,
                    )}
                    margin={{ left: -10, right: 8 }}
                    layout="vertical"
                  >
                    <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
                    <YAxis
                      dataKey="nombre"
                      type="category"
                      width={110}
                      tick={{ fontSize: 11 }}
                    />
                    <Tooltip
                      formatter={(v) => [`${v} min`, "Minutos"]}
                    />
                    <Bar
                      dataKey="minutos"
                      name="Minutos"
                      fill="#6366F1"
                      radius={[0, 4, 4, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>

                {/* Tabla detalle */}
                <div className="overflow-x-auto mt-4">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-100">
                        <th className="text-left py-2 px-3 text-xs font-semibold text-gray-500">
                          Técnico
                        </th>
                        <th className="text-right py-2 px-3 text-xs font-semibold text-gray-500">
                          Minutos
                        </th>
                        <th className="text-right py-2 px-3 text-xs font-semibold text-gray-500">
                          Horas
                        </th>
                        <th className="text-right py-2 px-3 text-xs font-semibold text-gray-500">
                          Costo M.O.
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {[...metricas.horasTecnico]
                        .sort((a, b) => b.minutos - a.minutos)
                        .map((tec, i) => (
                          <tr key={i} className="hover:bg-gray-50 transition-colors">
                            <td className="py-2.5 px-3 font-medium text-gray-800">
                              {tec.nombre || "Desconocido"}
                            </td>
                            <td className="py-2.5 px-3 text-right text-gray-600">
                              {tec.minutos}
                            </td>
                            <td className="py-2.5 px-3 text-right text-gray-600">
                              {(tec.minutos / 60).toFixed(1)}h
                            </td>
                            <td className="py-2.5 px-3 text-right font-semibold text-green-700">
                              $
                              {tec.costo.toLocaleString("es-CO", {
                                maximumFractionDigits: 0,
                              })}
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Estado vacío (sin data y sin loading) */}
        {!loading && !metricas && (
          <div className="flex flex-col items-center justify-center py-24 text-gray-400">
            <BarChart2 className="h-12 w-12 mb-3 opacity-30" />
            <p className="text-lg font-medium">Sin datos</p>
            <p className="text-sm">Ajusta los filtros o recarga la página</p>
          </div>
        )}
      </div>
    </div>
  );
}
